/**
 * Pin the add-in document to the visible task pane viewport.
 *
 * Office hosts often expand the iframe as content grows. If we keep re-measuring
 * after render, innerHeight tracks content height and overflow never triggers.
 * Lock the pane height on first measure; only update on window resize.
 */

import { useEffect } from 'react';

let lockedPaneHeight: number | null = null;

function readLockedFromDom(): number | null {
  const maxHeight = document.body.style.maxHeight;
  if (!maxHeight.endsWith('px')) {
    return null;
  }
  const parsed = Number.parseInt(maxHeight, 10);
  return parsed > 0 ? parsed : null;
}

function viewportHeight(): number {
  const visual = window.visualViewport?.height;
  if (visual != null && visual > 0) {
    return Math.round(visual);
  }
  const client = document.documentElement.clientHeight;
  if (client > 0) {
    return client;
  }
  return window.innerHeight;
}

function applyHeight(height: number): void {
  const { documentElement: html, body } = document;
  html.style.height = `${height}px`;
  html.style.maxHeight = `${height}px`;
  body.style.height = `${height}px`;
  body.style.maxHeight = `${height}px`;
}

/** Pin document height. Pass `fromResize: true` when the Word window was resized. */
export function applyTaskPaneViewport(fromResize = false): void {
  if (lockedPaneHeight == null) {
    lockedPaneHeight = readLockedFromDom();
  }
  const measured = viewportHeight();
  if (lockedPaneHeight == null || fromResize) {
    lockedPaneHeight = measured;
  }
  applyHeight(lockedPaneHeight);
}

export function useTaskPaneViewport(): void {
  useEffect(() => {
    applyTaskPaneViewport();

    const onResize = () => applyTaskPaneViewport(true);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, []);
}
